import { Divider, Slider } from "@material-ui/core";
import BN from "bn.js";
import {
  Dispatch,
  FunctionComponent,
  SetStateAction,
  useContext,
  useEffect,
} from "react";
import { Option, Some } from "space-monad";
import styled from "styled-components";
import { InfoButton } from "../../../../../components/button";
import { TextField } from "../../../../../components/forms/text";
import {
  Flex,
  Grid,
  hiddenDesktop,
  hiddenMobile,
} from "../../../../../components/layout";
import { StepperContext } from "../../../../../components/stepper/context";
import { device } from "../../../../../styles/media";
import { formatDecimal, formatEther } from "../../../../../utils/utils";
import { StakeContext } from "../../context";
import { denominator } from "../common";
import { useStakingForm } from "./form-hook";

const SLIDER_RESOLUTION = 100;

interface IFormProps {
  setAmount: Dispatch<SetStateAction<Option<BN>>>;
  amount: Option<BN>;
}

const StyledTextField = styled(TextField)`
  display: flex;
  margin: 1em auto;
  text-align: center;

  @media ${device.tablet} {
    padding: 0 2em;
  }
`;

const Amount = styled.h3`
  font-size: 1.4em;
  font-weight: 700;
  text-align: center;

  @media ${device.tablet} {
    margin-top: 2em;
  }
`;

const OptionButton = styled(InfoButton)`
  padding-left: 1em;
  padding-right: 1em;
`;

const StyledDivider = styled(Divider)`
  margin: 2em 0;
`;

const WrapperForm = styled.form`
  padding: 0 2em;
  max-width: 100%;

  @media ${device.tablet} {
    width: 30em;
    height: 20em;
  }
`;

const Options = styled(Grid).attrs({ ["col-xs"]: 3 })`
  ${hiddenDesktop};
  @media ${device.tablet} {
    padding: 0 2em;
  }
`;

const StyledSlider = styled(Slider)`
  ${hiddenMobile};
  display: block;
`;

const Balances = styled(Flex)`
  ${hiddenMobile};
  justify-content: space-between;
`;

const Form: FunctionComponent<IFormProps> = ({ setAmount, amount }) => {
  const { decimals, currentContract } = useContext(StakeContext);
  const { next, setValidation } = useContext(StepperContext);

  const {
    handleSubmit,
    fieldValue,
    setFieldValue,
    error,
    bnAmount,
  } = useStakingForm(amount);

  useEffect(() => {
    setValidation(() => () => handleSubmit(async (a) => setAmount(Some(a)))());
    return () => setValidation(() => () => Promise.resolve());
  }, [handleSubmit, setValidation]);

  return (
    <WrapperForm
      onSubmit={handleSubmit((amount) => {
        setAmount(Some(amount));
        next.next();
      })}
    >
      {decimals.fold(
        () => null,
        (decimalActual) => (
          <Options>
            {["50000", "70000", "100000", "200000", "500000", "1000000"].map(
              (x) => (
                <OptionButton
                  onClick={() => setFieldValue(x)}
                  key={x}
                  active={fieldValue === x}
                >
                  {formatDecimal(
                    new BN(`${x}${Array(decimalActual + 1).join("0")}`)
                  )}
                </OptionButton>
              )
            )}
          </Options>
        )
      )}
      {currentContract
        .map((x) => x.walletInfo)
        .fold(
          () => null,
          ({ minStake, maxStake }) => (
            <>
              <Balances>
                <span>{formatEther(minStake)}</span>
                <span>{formatEther(maxStake)}</span>
              </Balances>
              <StyledSlider
                value={parseFloat(
                  bnAmount
                    .sub(minStake)
                    .muln(SLIDER_RESOLUTION)
                    .div(maxStake.sub(minStake))
                    .toString()
                )}
                onChange={(e, val) =>
                  setFieldValue(
                    new BN(val)
                      .mul(maxStake.sub(minStake))
                      .divn(SLIDER_RESOLUTION)
                      .add(minStake)
                      .div(denominator)
                      .toString()
                  )
                }
                orientation="horizontal"
                min={0}
                max={SLIDER_RESOLUTION}
              />
            </>
          )
        )}
      <StyledDivider />
      <StyledTextField
        value={fieldValue}
        fullWidth
        onChange={(e) => {
          setFieldValue(e.target.value);
        }}
        error={!!error}
        helperText={error}
      />
      <Amount>
        Lock $SKRT {formatDecimal(bnAmount, decimals.getOrElse(0))}
      </Amount>
    </WrapperForm>
  );
};

export default Form;
